import { useEffect, useState } from "react";
import api, { formatErr, API } from "@/lib/api";
import {
  Crosshair, MagnifyingGlass, Trophy, ArrowsClockwise, DownloadSimple,
  PaperPlaneTilt, Star, Globe, Phone, MapPin, CheckSquare, Square,
  Funnel, Sparkle, X, WarningCircle, ChatCircleDots, ChartBar,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIMARY = "#2D5BFF";
const ACCENT  = "#65CE95";

const DEFAULT_FILTERS = {
  min_reviews: 0,
  min_rating: 0,
  min_rank: 4,
  max_rank: 30,
  has_website: null,     // null = either, true/false = require/exclude
  website_broken: null,  // null = ignore, true = only include broken/parked
  include_top_3: false,
};

// Named rank presets — sales strategy shortcuts. Each agency chooses its own.
const RANK_PRESETS = [
  { key: "close", label: "🎯 Close to Top #4–10", min: 4, max: 10 },
  { key: "motivated", label: "🔥 Motivated #11–30", min: 11, max: 30 },
  { key: "mid", label: "Mid-market #11–20", min: 11, max: 20 },
  { key: "longtail", label: "Long-tail #21–30", min: 21, max: 30 },
  { key: "full", label: "Full report #4–30", min: 4, max: 30 },
];

export default function Prospecting() {
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [source, setSource] = useState(null);
  const [note, setNote] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [recentSearches, setRecentSearches] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [gapForId, setGapForId] = useState(null);
  const [callReasonForId, setCallReasonForId] = useState(null);
  const [lastSearchId, setLastSearchId] = useState(null);
  const [lastSearchContext, setLastSearchContext] = useState(null); // {keyword, city, state}

  useEffect(() => { loadRecent(); }, []);

  const loadRecent = async () => {
    try {
      const { data } = await api.get("/prospecting/searches");
      setRecentSearches(data.searches || []);
    } catch { /* silent */ }
  };

  const runSearch = async () => {
    if (!keyword.trim() || !city.trim() || !state.trim()) {
      toast.error("Keyword, city, and state are all required");
      return;
    }
    setRunning(true); setResults([]); setSelectedIds(new Set()); setSource(null); setNote(null);
    try {
      const { data } = await api.post("/prospecting/search", {
        keyword, city, state,
        filters,
        save: true,
        label: `${keyword} · ${city}, ${state}`,
      });
      if (data.success) {
        setResults(data.results || []);
        setSource(data.source);
        setNote(data.note);
        setLastSearchId(data.search_id || null);
        setLastSearchContext({ keyword, city, state });
        toast.success(`Found ${data.filtered_count} prospects (saved ${data.saved_count})`);
        loadRecent();
      } else {
        toast.error(data.message || "Search failed");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setRunning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(results.map((r) => r.place_id)));
  const selectNone = () => setSelectedIds(new Set());

  // Frontend selection is by place_id (always present); but the push endpoint expects DB prospect ids.
  // We re-fetch the prospect ids for the selected place_ids before pushing.
  const pushToIntentBrain = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one prospect");
      return;
    }
    setPushing(true);
    try {
      // Pull saved prospects matching the place_ids
      const { data: pdata } = await api.get("/prospecting/prospects?limit=500");
      const matched = (pdata.prospects || []).filter((p) => selectedIds.has(p.place_id));
      const prospect_ids = matched.map((p) => p.id);
      if (prospect_ids.length === 0) {
        toast.error("Couldn't match selected prospects to saved records — try re-running search first");
        return;
      }
      const { data } = await api.post("/prospecting/push-to-intentbrain", { prospect_ids });
      if (data.success) {
        toast.success(`Pushed ${data.pushed} prospect(s) to IntentBrain`);
      } else {
        toast.error(data.message || data.error || "Push failed");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setPushing(false);
    }
  };

  const downloadCsv = (format = "full") => {
    // CSV must exactly mirror the currently displayed Prospecting results —
    // no cross-keyword contamination, no ranks outside the selected window.
    // active search_id (primary) + keyword/city/state (fallback) + explicit
    // rank window (belt-and-suspenders).
    if (results.length === 0 || !lastSearchContext) {
      toast.error("Run a search first, then export the CSV of that search.");
      return;
    }
    const params = new URLSearchParams();
    if (lastSearchId) params.set("search_id", lastSearchId);
    if (lastSearchContext?.keyword) params.set("keyword", lastSearchContext.keyword);
    if (lastSearchContext?.city) params.set("city", lastSearchContext.city);
    if (lastSearchContext?.state) params.set("state", lastSearchContext.state);
    // CSV must honor the currently active rank range — never hardcode.
    params.set("min_rank", String(filters.min_rank || 4));
    params.set("max_rank", String(filters.max_rank || 30));
    if (filters.include_top_3) params.set("include_top_3", "true");
    // Stacked website filters — CSV mirrors the live filter state so
    // toggling chips after a search still produces the right export.
    if (filters.has_website === true) params.set("has_website", "true");
    if (filters.has_website === false) params.set("has_website", "false");
    if (filters.website_broken === true) params.set("website_broken", "true");
    if (format === "intentbrain") params.set("format", "intentbrain");
    if (format === "playbook") params.set("format", "playbook");
    const url = `${API}/prospecting/prospects/csv?${params.toString()}`;
    const token = localStorage.getItem("token");
    const kwSlug = (lastSearchContext?.keyword || "prospects").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    const citySlug = (lastSearchContext?.city || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    let safeName;
    if (format === "intentbrain") {
      safeName = `intentbrain_${kwSlug}_${citySlug}.csv`;
    } else if (format === "playbook") {
      safeName = `sales_playbook_${kwSlug}_${citySlug}.csv`;
    } else {
      safeName = `ranktrust_${kwSlug}_${citySlug}.csv`;
    }
    // Open with a one-shot fetch + blob so we can include the Authorization header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(b);
        a.href = u; a.download = safeName; a.click();
        URL.revokeObjectURL(u);
        const msg =
          format === "intentbrain" ? "IntentBrain-ready CSV downloaded" :
          format === "playbook" ? "Sales Playbook CSV downloaded" :
          "Full analysis CSV downloaded";
        toast.success(msg);
      })
      .catch(() => toast.error("CSV download failed"));
  };

  return (
    <div className="space-y-6" data-testid="prospecting-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-tiny mb-1">Outbound Prospecting</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight">Find businesses outside the Top-3</h1>
          <p className="mt-1 text-sm text-zinc-500 max-w-2xl">
            Search any keyword + city → instantly see the local GBP businesses ranked 4-30.
            Push qualified prospects directly to IntentBrain for AI cold outreach.
          </p>
        </div>
      </div>

      {/* Search form */}
      <div className="card p-5 lg:p-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label icon={<Crosshair size={12} weight="bold" />}>Keyword</Label>
            <input data-testid="prospecting-keyword" className="input-zen" placeholder="e.g. roofing company"
              value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label icon={<MapPin size={12} weight="bold" />}>City</Label>
            <input data-testid="prospecting-city" className="input-zen" placeholder="Atlanta"
              value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div>
            <Label>State</Label>
            <input data-testid="prospecting-state" className="input-zen" placeholder="GA"
              value={state} onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 3))} />
          </div>
          <div className="flex items-end">
            <button data-testid="prospecting-search-btn" disabled={running} onClick={runSearch}
              className="btn-primary w-full">
              {running ? <><span className="spinner"></span> Searching…</> : <><MagnifyingGlass size={14} weight="bold" /> Search</>}
            </button>
          </div>
        </div>

        {/* Signature quick-filter presets */}
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="prospecting-presets">
          <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500 mr-1">Quick filters:</span>
          <PresetChip
            active={isEasyWinsActive(filters)}
            onClick={() => setFilters((f) => (isEasyWinsActive(f) ? { ...f, min_rank: 4, max_rank: 30 } : { ...f, min_rank: 4, max_rank: 10 }))}
            testId="preset-easy-wins"
            tone="purple"
          >
            ⚡ Easy Wins
          </PresetChip>
          <PresetChip
            active={filters.has_website === false}
            onClick={() => setFilters((f) => ({ ...f, has_website: f.has_website === false ? null : false, website_broken: null }))}
            testId="preset-no-website"
            tone="amber"
          >
            🌐 No Website Detected
          </PresetChip>
          <PresetChip
            active={filters.website_broken === true}
            onClick={() => setFilters((f) => ({ ...f, website_broken: f.website_broken === true ? null : true, has_website: null }))}
            testId="preset-broken-website"
            tone="red"
          >
            ⚠️ Website Broken
          </PresetChip>
          <PresetChip
            active={(filters.min_reviews || 0) >= 20 && (filters.min_rating || 0) >= 4.0}
            onClick={() => setFilters((f) => {
              const on = (f.min_reviews || 0) >= 20 && (f.min_rating || 0) >= 4.0;
              return on ? { ...f, min_reviews: 0, min_rating: 0 } : { ...f, min_reviews: 20, min_rating: 4.0 };
            })}
            testId="preset-high-trust"
            tone="emerald"
          >
            ⭐ High Trust (4.0+ · 20+ reviews)
          </PresetChip>
          {RANK_PRESETS.map((rp) => {
            const active = (filters.min_rank || 4) === rp.min && (filters.max_rank || 30) === rp.max;
            return (
              <PresetChip
                key={rp.key}
                active={active}
                onClick={() => setFilters((f) => active
                  ? { ...f, min_rank: 4, max_rank: 30 }
                  : { ...f, min_rank: rp.min, max_rank: rp.max })}
                testId={`preset-rank-${rp.key}`}
                tone="blue"
              >
                {rp.label}
              </PresetChip>
            );
          })}
          {(filters.has_website !== null || filters.website_broken !== null || filters.min_reviews > 0 || filters.min_rating > 0 || (filters.min_rank || 4) !== 4 || (filters.max_rank || 30) !== 30 || filters.include_top_3) && (
            <button
              onClick={() => setFilters(DEFAULT_FILTERS)}
              className="text-[11px] font-semibold text-zinc-500 hover:text-zinc-900 underline underline-offset-2 ml-1"
              data-testid="preset-clear"
            >
              Clear
            </button>
          )}
        </div>

        {/* Filters */}
        <details className="mt-4 group">
          <summary className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 cursor-pointer inline-flex items-center gap-1.5 hover:text-zinc-800">
            <Funnel size={12} weight="bold" /> Filters
          </summary>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <NumberField label="Min reviews" value={filters.min_reviews} onChange={(v) => setFilters({ ...filters, min_reviews: v })} testId="filter-min-reviews" />
            <NumberField label="Min rating" step="0.1" value={filters.min_rating} onChange={(v) => setFilters({ ...filters, min_rating: v })} testId="filter-min-rating" />
            <NumberField label="Start rank" value={filters.min_rank} onChange={(v) => setFilters({ ...filters, min_rank: v })} testId="filter-min-rank" />
            <NumberField label="End rank" value={filters.max_rank} onChange={(v) => setFilters({ ...filters, max_rank: v })} testId="filter-max-rank" />
            <div className="md:col-span-2">
              <Label>Website</Label>
              <select className="input-zen" value={filters.has_website === null ? "any" : filters.has_website ? "yes" : "no"}
                onChange={(e) => setFilters({ ...filters, has_website: e.target.value === "any" ? null : e.target.value === "yes" })}>
                <option value="any">Any</option>
                <option value="yes">Has website</option>
                <option value="no">No website</option>
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={filters.include_top_3} onChange={(e) => setFilters({ ...filters, include_top_3: e.target.checked })} />
                Include businesses already in top-3 (default: exclude)
              </label>
            </div>
          </div>
        </details>
      </div>

      {/* Source badge + bulk action bar */}
      {(results.length > 0 || note) && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-3 text-sm">
            {source === "mock" && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase px-2 py-1 rounded bg-amber-100 text-amber-800">
                MOCK MODE
              </span>
            )}
            {source === "serpapi" && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase px-2 py-1 rounded bg-green-100 text-green-800">
                <Sparkle size={11} weight="fill" /> Live SerpAPI
              </span>
            )}
            <span className="text-zinc-600">{results.length} prospects</span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-600">{selectedIds.size} selected</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={selectAll} className="btn-ghost text-xs" data-testid="prospecting-select-all">Select all</button>
            <button onClick={selectNone} className="btn-ghost text-xs">Clear</button>
            <button
              onClick={() => downloadCsv("intentbrain")}
              disabled={results.length === 0}
              className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="prospecting-export-intentbrain"
              title="Clean CSV ready to upload directly into IntentBrain — no editing required"
            >
              🚀 Export to IntentBrain
            </button>
            <button
              onClick={() => downloadCsv("playbook")}
              disabled={results.length === 0}
              className="btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="prospecting-export-playbook"
              title="Actionable sales list — sorted by rank, with call priority, why-call, and suggested opener columns"
            >
              <DownloadSimple size={12} weight="bold" /> Sales Playbook CSV
            </button>
            <button
              onClick={() => downloadCsv("full")}
              disabled={results.length === 0}
              className="btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="prospecting-csv"
              title="Full analysis columns — includes place_id, GPS, timestamps, and metadata"
            >
              <DownloadSimple size={12} weight="bold" /> Full Analysis CSV
            </button>
            <button onClick={pushToIntentBrain} disabled={pushing || selectedIds.size === 0} className="btn-primary text-xs" data-testid="prospecting-push">
              <PaperPlaneTilt size={12} weight="bold" /> Push to IntentBrain
            </button>
          </div>
        </div>
      )}
      {note && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">{note}</div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="prospecting-results-table">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left">Pick</th>
                  <th className="px-3 py-2 text-left">Rank</th>
                  <th className="px-3 py-2 text-left">Business</th>
                  <th className="px-3 py-2 text-left">Rating</th>
                  <th className="px-3 py-2 text-left">Reviews</th>
                  <th className="px-3 py-2 text-left">Opportunity</th>
                  <th className="px-3 py-2 text-left">+Leads/mo</th>
                  <th className="px-3 py-2 text-left">Contact</th>
                </tr>
              </thead>
              <tbody>
                {results.map((b) => (
                  <tr key={b.place_id} className="border-t border-zinc-100 hover:bg-zinc-50/60">
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleSelect(b.place_id)} className="hover:text-blue-600 transition-colors" data-testid={`prospecting-pick-${b.current_rank}`}>
                        {selectedIds.has(b.place_id)
                          ? <CheckSquare size={20} weight="fill" style={{ color: PRIMARY }} />
                          : <Square size={20} />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-black tabular-nums ${b.current_rank <= 3 ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700"}`}>#{b.current_rank}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-zinc-900">{b.business_name}</span>
                        {b.has_website === false && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="badge-no-website" title="No website linked to Google Business Profile — prime prospect for a modern website + RankTrust bundle">
                            🌐 No Website
                          </span>
                        )}
                        {b.website_broken === true && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-800" data-testid="badge-broken-website" title="Website is unreachable, parked, or returns an error — immediate value conversation">
                            ⚠️ Broken
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 truncate max-w-[280px]">{b.address}</div>
                      {b.storm_opportunity && (() => {
                        const p = b.storm_opportunity.priority;
                        const isImmediate = p === "Immediate" || p === "Call Immediately";
                        const dot = isImmediate ? "🔴" : p === "High" ? "🟠" : "🟡";
                        const color = isImmediate ? "text-red-800" : p === "High" ? "text-orange-800" : "text-amber-800";
                        return (
                          <div className={`mt-1 text-xs font-medium ${color}`} data-testid="storm-line">
                            {dot} {isImmediate ? "Immediate" : p} · Recent {b.storm_opportunity.description || b.storm_opportunity.storm_type} reported {b.storm_opportunity.days_ago} days ago within {b.storm_opportunity.miles} miles.
                          </div>
                        );
                      })()}
                      <div className="mt-1.5 flex items-center gap-1">
                        <button
                          onClick={() => setGapForId(b.id)}
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition inline-flex items-center gap-1"
                          data-testid={`btn-gap-${b.id}`}
                        >
                          <ChartBar size={11} weight="bold" /> Gap
                        </button>
                        <button
                          onClick={() => setCallReasonForId(b.id)}
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 transition inline-flex items-center gap-1"
                          data-testid={`btn-callreason-${b.id}`}
                        >
                          <ChatCircleDots size={11} weight="bold" /> Why call?
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="inline-flex items-center gap-1 text-zinc-700">
                        <Star size={12} weight="fill" style={{ color: "#F59E0B" }} /> {b.rating ?? "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-700 tabular-nums">{b.review_count ?? "—"}</td>
                    <td className="px-3 py-2.5">
                      <OpportunityBar score={b.opportunity_score} />
                    </td>
                    <td className="px-3 py-2.5 tabular-nums font-semibold" style={{ color: ACCENT }}>+{b.estimated_lead_gain_per_month}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {b.phone && <a href={`tel:${b.phone}`} className="block text-zinc-700 hover:text-blue-600"><Phone size={11} className="inline mr-1" />{b.phone}</a>}
                      {b.website && <a href={b.website} target="_blank" rel="noopener noreferrer" className="block text-zinc-500 hover:text-blue-600 truncate max-w-[180px]"><Globe size={11} className="inline mr-1" />Site</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent searches */}
      {recentSearches.length > 0 && (
        <div className="card p-5">
          <div className="label-tiny mb-3 inline-flex items-center gap-1.5"><ArrowsClockwise size={11} weight="bold" /> Recent Searches</div>
          <div className="space-y-1.5">
            {recentSearches.slice(0, 6).map((s) => (
              <button
                key={s.id}
                onClick={() => { setKeyword(s.keyword); setCity(s.city); setState(s.state); }}
                className="w-full text-left text-sm flex flex-wrap items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-zinc-50 transition-colors"
              >
                <span className="font-semibold text-zinc-800">{s.label}</span>
                <span className="text-xs text-zinc-500 tabular-nums">{s.filtered_count} prospects · {new Date(s.created_at).toLocaleDateString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!running && results.length === 0 && (
        <div className="card p-10 text-center">
          <Trophy size={32} weight="duotone" className="mx-auto text-zinc-300" />
          <h2 className="mt-3 text-lg font-bold tracking-tight">Find prospects outside the Top-3</h2>
          <p className="mt-1 text-sm text-zinc-500 max-w-md mx-auto">
            Enter a keyword + city above. We&apos;ll scan Google Maps for businesses ranked 4-30 in
            that market — the ones who&apos;d benefit most from your agency.
          </p>
        </div>
      )}

      {gapForId && <GapModal prospectId={gapForId} onClose={() => setGapForId(null)} />}
      {callReasonForId && <CallReasonModal prospectId={callReasonForId} prospect={results.find((r) => r.id === callReasonForId)} onClose={() => setCallReasonForId(null)} />}
    </div>
  );
}

function GapModal({ prospectId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/prospecting/prospects/${prospectId}/gap`);
        setData(d);
      } catch (e) { setErr(formatErr(e)); }
    })();
  }, [prospectId]);
  return (
    <ModalShell title="Competitor Gap Analysis" onClose={onClose} testId="gap-modal">
      {err && <div className="text-sm text-red-700 bg-red-50 p-3 rounded">{err}</div>}
      {!err && !data && <div className="text-sm text-zinc-500">Loading…</div>}
      {data && (
        <div className="space-y-4">
          {/* Prospect header + Close Probability */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-1">Your prospect</div>
              <div className="font-bold text-lg leading-tight">{data.prospect.business_name}</div>
              <div className="text-sm text-zinc-600">
                Rank <span className="font-bold text-zinc-900">#{data.prospect.current_rank}</span> · {data.prospect.review_count} reviews · {data.prospect.rating}★
              </div>
            </div>
            <ScoreDial value={data.close_probability} label="Close Probability" testId="close-probability" />
          </div>

          {(data.prospect.opportunity_score || data.close_probability_reasons?.length > 0) && (
            <div className="rounded-lg p-3 bg-zinc-50 border border-zinc-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500">Signals</span>
                {data.prospect.opportunity_score != null && (
                  <span className="text-xs text-zinc-600">Opportunity Score <span className="font-bold text-zinc-900 tabular-nums">{data.prospect.opportunity_score}</span></span>
                )}
              </div>
              <ul className="text-xs text-zinc-700 space-y-1">
                {(data.close_probability_reasons || []).map((r, i) => (
                  <li key={i} className="flex gap-1.5"><span className="text-emerald-600 font-bold">✓</span><span>{r}</span></li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Top-3 in this market</div>
            <div className="space-y-1.5">
              {(data.top3 || []).map((t) => (
                <div key={t.place_id || t.current_rank} className="flex items-center justify-between p-2 rounded bg-zinc-50 border border-zinc-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shrink-0">#{t.current_rank}</span>
                    <span className="text-sm font-semibold truncate">{t.business_name}</span>
                  </div>
                  <div className="text-xs text-zinc-600 tabular-nums shrink-0 ml-2">{t.review_count} · {t.rating}★</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Review gap to #3" value={data.review_gap_to_third} suffix="reviews to close" />
            <StatCard label="Est. lead gain if top-3" value={`+${data.estimated_lead_gain_if_top3}`} suffix="calls / month" positive />
          </div>
          <div className="text-xs text-zinc-500">
            Top-3 in this market collectively capture ~{data.estimated_top3_calls_per_month} calls / month.
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function CallReasonModal({ prospectId, prospect, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.post(`/prospecting/prospects/${prospectId}/call-reason`);
        setData(d);
      } catch (e) { setErr(formatErr(e)); }
      setLoading(false);
    })();
  }, [prospectId]);

  const copyScript = () => {
    if (!data) return;
    const cs = data.conversation_strategy || {};
    const offers = (data.ranked_offers || []).map((o, i) => `${i + 1}. ${o.offer}\n   Reason: ${o.reason}`).join("\n");
    const opener = data.approved_opener || data.opener || "";
    const feasibility = (data.feasibility_level || "").toUpperCase();
    const bulletList = (arr) => (arr || []).map((x) => `• ${x}`).join("\n");
    const script = [
      `Prospect: ${prospect?.business_name || ""}`,
      prospect?.current_rank ? `Current Rank: #${prospect.current_rank}` : null,
      prospect?.city && prospect?.state ? `Market: ${prospect.city}, ${prospect.state} (${prospect?.keyword || ""})` : null,
      data.storm_line ? `STORM SIGNAL: ${data.storm_line}` : null,
      "",
      data.diagnosis_type ? `DIAGNOSIS TYPE: ${data.diagnosis_type}` : null,
      data.primary_constraint ? `PRIMARY CONSTRAINT: ${data.primary_constraint}` : null,
      feasibility ? `FEASIBILITY: ${feasibility}` : null,
      data.recommended_offer ? `RECOMMENDED OFFER: ${data.recommended_offer}` : null,
      "",
      "APPROVED OPENER",
      opener ? `"${opener}"` : "(no opener generated)",
      "",
      (data.discovery_questions || []).length ? "DISCOVERY QUESTIONS (ask BEFORE recommending anything)" : null,
      bulletList(data.discovery_questions) || null,
      "",
      (data.safe_claims || []).length ? "SAFE CLAIMS — factually defensible" : null,
      bulletList(data.safe_claims) || null,
      "",
      (data.prohibited_claims || []).length ? "PROHIBITED CLAIMS — NEVER say on this call" : null,
      bulletList(data.prohibited_claims) || null,
      "",
      (data.supporting_levers || []).length ? "SUPPORTING LEVERS" : null,
      bulletList(data.supporting_levers) || null,
      "",
      (data.reasons || []).length ? "TALKING POINTS" : null,
      bulletList(data.reasons) || null,
      "",
      offers ? "RANKED OFFERS (by achievability, not gap size)" : null,
      offers || null,
      "",
      cs.decision_maker_style ? "CONVERSATION STRATEGY" : null,
      cs.decision_maker_style ? `Decision Maker Style: ${cs.decision_maker_style}` : null,
      cs.lead_with ? `Lead with: ${cs.lead_with}` : null,
      cs.avoid ? `Avoid: ${cs.avoid}` : null,
      cs.primary_goal ? `Primary Goal: ${cs.primary_goal}` : null,
      "",
      "— Generated by RankTrust Sales Intelligence (feasibility-aware)",
    ].filter((x) => x !== null).join("\n");
    navigator.clipboard.writeText(script).then(
      () => toast.success("Sales script copied to clipboard"),
      () => toast.error("Copy failed — select and copy manually")
    );
  };

  return (
    <ModalShell title={`Why call ${prospect?.business_name || "this business"} today?`} onClose={onClose} testId="callreason-modal">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <span className="inline-block w-4 h-4 border-2 border-zinc-300 border-t-purple-600 rounded-full animate-spin"></span>
          Generating with Claude…
        </div>
      )}
      {err && (
        <div className="text-sm text-red-700 bg-red-50 p-3 rounded">
          <div className="font-bold mb-1">AI generation failed</div>
          <div>{err}</div>
        </div>
      )}
      {data && (
        <div className="space-y-4">
          {data.storm_line && (() => {
            const p = (data.storm_opportunity || {}).priority;
            const isImmediate = p === "Immediate" || p === "Call Immediately";
            const dot = isImmediate ? "🔴" : p === "High" ? "🟠" : "🟡";
            const bg = isImmediate ? "border-red-200 bg-red-50 text-red-900" : p === "High" ? "border-orange-200 bg-orange-50 text-orange-900" : "border-amber-200 bg-amber-50 text-amber-900";
            return (
              <div className={`rounded-lg border p-3 text-sm ${bg}`} data-testid="storm-line-modal">
                <span className="font-bold">{dot} Storm signal:</span> {data.storm_line}
              </div>
            );
          })()}
          {(data.diagnosis_type || data.feasibility_level || data.primary_constraint) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="feasibility-summary">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                {data.diagnosis_type && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-amber-300 font-bold uppercase tracking-wider text-amber-900">
                    {data.diagnosis_type}
                  </span>
                )}
                {data.feasibility_level && (
                  <span
                    className={
                      "inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider " +
                      (data.feasibility_level === "high"
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : data.feasibility_level === "medium"
                        ? "bg-amber-100 text-amber-900 border border-amber-300"
                        : "bg-red-100 text-red-800 border border-red-300")
                    }
                    data-testid="feasibility-badge"
                  >
                    Feasibility: {data.feasibility_level}
                  </span>
                )}
              </div>
              {data.primary_constraint && (
                <div className="mt-2 text-sm text-amber-900">
                  <span className="font-bold">Primary constraint:</span> {data.primary_constraint}
                </div>
              )}
              {data.recommended_offer && (
                <div className="mt-1 text-sm text-amber-900">
                  <span className="font-bold">Recommended offer:</span> {data.recommended_offer}
                </div>
              )}
            </div>
          )}

          {(data.approved_opener || data.opener) && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Approved opener</div>
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-sm text-purple-900 italic" data-testid="approved-opener">
                &ldquo;{data.approved_opener || data.opener}&rdquo;
              </div>
            </div>
          )}

          {(data.discovery_questions || []).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Discovery questions (ask BEFORE recommending)</div>
              <ul className="space-y-1.5" data-testid="discovery-questions">
                {data.discovery_questions.map((q, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-blue-600 font-bold">?</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.safe_claims || []).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-emerald-700 mb-2">Safe claims</div>
              <ul className="space-y-1.5" data-testid="safe-claims">
                {data.safe_claims.map((c, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-emerald-600 font-bold">✓</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.prohibited_claims || []).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-red-700 mb-2">Prohibited claims — never say</div>
              <ul className="space-y-1.5" data-testid="prohibited-claims">
                {data.prohibited_claims.map((c, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.supporting_levers || []).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Supporting levers</div>
              <div className="flex flex-wrap gap-1.5" data-testid="supporting-levers">
                {data.supporting_levers.map((l, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-900">
                    {l}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(data.reasons || []).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Talking points</div>
              <ul className="space-y-1.5">
                {data.reasons.map((r, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-emerald-600 font-bold">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(data.ranked_offers || []).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Ranked offers (achievability-first)</div>
              <ol className="space-y-2">
                {data.ranked_offers.map((o, i) => (
                  <li key={i} className="flex gap-3 p-2.5 rounded-lg bg-blue-50 border border-blue-200" data-testid={`ranked-offer-${i + 1}`}>
                    <span className="w-6 h-6 shrink-0 rounded-full bg-blue-600 text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                    <div>
                      <div className="text-sm font-bold text-blue-900">{formatOfferLabel(o.offer)}</div>
                      <div className="text-xs text-blue-800 mt-0.5">{o.reason}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {data.conversation_strategy && Object.keys(data.conversation_strategy).length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase text-zinc-500 mb-2">Conversation strategy</div>
              <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-200" data-testid="conversation-strategy">
                {data.conversation_strategy.decision_maker_style && <StrategyRow label="Decision Maker Style" value={data.conversation_strategy.decision_maker_style} />}
                {data.conversation_strategy.lead_with && <StrategyRow label="Lead with" value={data.conversation_strategy.lead_with} />}
                {data.conversation_strategy.avoid && <StrategyRow label="Avoid" value={data.conversation_strategy.avoid} negative />}
                {data.conversation_strategy.primary_goal && <StrategyRow label="Primary Goal" value={data.conversation_strategy.primary_goal} positive />}
              </div>
            </div>
          )}

          <div className="pt-2 border-t border-zinc-200 flex justify-end">
            <button
              onClick={copyScript}
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition"
              data-testid="copy-sales-script"
            >
              📋 Copy Sales Script
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function StrategyRow({ label, value, positive, negative }) {
  const dotColor = positive ? "bg-emerald-500" : negative ? "bg-red-500" : "bg-blue-500";
  return (
    <div className="flex gap-3 p-2.5 items-start">
      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${dotColor}`}></span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold tracking-wider uppercase text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-900">{value}</div>
      </div>
    </div>
  );
}

function ScoreDial({ value, label, testId }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color = pct >= 75 ? "#10A37F" : pct >= 55 ? "#3B82F6" : pct >= 35 ? "#F59E0B" : "#9CA3AF";
  return (
    <div className="rounded-lg p-3 bg-white border-2 flex flex-col items-center justify-center" style={{ borderColor: color }} data-testid={testId}>
      <div className="text-3xl font-black tabular-nums leading-none" style={{ color }}>{pct}%</div>
      <div className="text-[9px] font-bold tracking-wider uppercase mt-1 text-center leading-tight" style={{ color }}>{label}</div>
    </div>
  );
}

function formatOfferLabel(offer) {
  if (!offer) return "Offer";
  return offer
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function ModalShell({ title, children, onClose, testId }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid={testId}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-200 flex items-start justify-between">
          <h3 className="font-bold text-base">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid={`${testId}-close`}>
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, positive }) {
  return (
    <div className={`rounded-lg p-3 border ${positive ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200"}`}>
      <div className="text-[10px] font-bold tracking-wider uppercase text-zinc-500">{label}</div>
      <div className={`text-2xl font-black tabular-nums mt-0.5 ${positive ? "text-emerald-700" : "text-zinc-900"}`}>{value}</div>
      <div className="text-[11px] text-zinc-500">{suffix}</div>
    </div>
  );
}

function isEasyWinsActive(f) {
  return (f.min_rank || 4) === 4 && f.max_rank === 10 && f.min_reviews === 0 && f.min_rating === 0 && f.has_website === null && f.website_broken === null;
}

function Label({ children, icon }) {
  return (
    <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500 mb-1.5 flex items-center gap-1.5">
      {icon}{children}
    </label>
  );
}

function NumberField({ label, value, onChange, step = "1", testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="number"
        step={step}
        className="input-zen"
        value={value || ""}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        data-testid={testId}
      />
    </div>
  );
}

function OpportunityBar({ score }) {
  const color = score >= 80 ? "#10A37F" : score >= 60 ? "#65CE95" : score >= 40 ? "#F59E0B" : "#9CA3AF";
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${score}%`, background: color }}></div>
      </div>
      <span className="text-xs font-bold tabular-nums" style={{ color }}>{score}</span>
    </div>
  );
}

function PresetChip({ children, active, onClick, tone = "blue", testId }) {
  const tones = {
    amber:   { on: "bg-amber-500 text-white border-amber-500",       off: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100" },
    emerald: { on: "bg-emerald-600 text-white border-emerald-600",   off: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100" },
    blue:    { on: "bg-blue-600 text-white border-blue-600",         off: "bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100" },
    purple:  { on: "bg-purple-600 text-white border-purple-600",     off: "bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100" },
    red:     { on: "bg-red-600 text-white border-red-600",           off: "bg-red-50 text-red-800 border-red-200 hover:bg-red-100" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all ${active ? t.on : t.off}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
